import { Component, EventEmitter, Output, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, of, Subject, switchMap, takeUntil } from 'rxjs';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { ProjectService } from 'src/app/services/project.service';
import { CheckExistsService } from 'src/app/services/check-exists.service';

import { ButtonModule } from 'src/app/shared/buttons/button.module';
import { AppConstants } from 'src/app/app.constants';

const IMPORTS = [CommonModule, ReactiveFormsModule, ButtonModule];

@Component({
    selector: 'app-create-project',
    standalone: true,
    imports: [...IMPORTS],
    templateUrl: './create-project.component.html',
    styleUrl: './create-project.component.css'
})
export class CreateProjectComponent implements OnDestroy {
    @Output() projectCreated = new EventEmitter<string>();
    @ViewChild('createProjectModalBox') createProjectModalBox: any;

    private destroy$ = new Subject<void>();

    nameControl = new FormControl('', [
        Validators.required,
        Validators.maxLength(20),
        Validators.pattern(/^[A-Za-z][A-Za-z0-9_]*$/)
    ]);

    nameExists: boolean | null = null;
    checking: boolean = false;

    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
        private projectService: ProjectService,
        private checkExistsService: CheckExistsService
    ) {
        this.listenToNameChanges();
    }

    openModal() {
        this.modalService.open(this.createProjectModalBox, {
            backdrop: "static",
            keyboard: false,
            centered: true,
            size: "sm",
            windowClass: "modal-holder",
        }).result.finally(() => {
            this.nameControl.reset();
            this.nameExists = null;
            this.checking = false;
        });
    }

    onNameInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const cleaned = input.value.replace(/[^A-Za-z0-9_]/g, '');
        this.nameControl.setValue(cleaned, { emitEvent: false });
    }

    isInvalid(): boolean {
        return this.nameControl.touched && this.nameControl.invalid;
    }

    hasError(errorType: string): boolean {
        return this.nameControl.hasError(errorType);
    }

    listenToNameChanges() {
        this.nameControl.valueChanges.pipe(
            map((name) => name?.trim() ?? ''),
            debounceTime(AppConstants.debounceTime),
            distinctUntilChanged(),
            switchMap((name) => {
                if (!name) {
                    this.nameExists = null;
                    this.checking = false;
                    return of(null);
                }
                this.checking = true;
                return this.checkExistsService.checkExists('project', name, '');
            }),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (res) => {
                if (res) this.nameExists = res.exists;
                this.checking = false;
            },
            error: () => {
                this.nameExists = null;
                this.checking = false;
            }
        });
    }

    createProject() {
        if (this.checking) return;
        if (this.nameExists) return;

        if (this.nameControl.invalid) {
            this.nameControl.markAsTouched();
            return;
        }

        const projectData = { name: this.nameControl.value };

        this.projectService.createProject(projectData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.toastr.success('Success', 'Project created Successfully');
                    this.projectCreated.emit(response._id);
                    this.modalService.dismissAll();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}